import AlertModal from '@betterfunnel/ui-core/AlertModal';
import Badge from '@betterfunnel/ui-core/Badge';
import Button from '@betterfunnel/ui-core/Button';
import Loader from '@betterfunnel/ui-core/Loader';
import MenuButton from '@betterfunnel/ui-core/MenuButton';
import Tabs, { Tab } from '@betterfunnel/ui-core/Tabs';
import Lottie from 'lottie-react';
import { useContext, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import websiteBuildingAnimation from '../../assets/lotties/website-building.json';
import NoVariantExists from '../../components/DynamicPages/NoVariantExists';
import { DashboardContext } from '../../contexts/DashboardContext';
import { DynamicPageContext } from '../../contexts/DynamicPageContext';
import CopyDynamicPageLinkModal from '../../modals/CopyDynamicPageLinkModal';
import CreateVariantModal, {
  CreateVariantData,
} from '../../modals/CreateVariantModal';
import DynamicPageVersionsHistoryDrawer from '../../modals/DynamicPageVersionsHistoryDrawer';
import EditorModal from '../../modals/EditorModal';
import EditVariantConstraintsModal from '../../modals/EditVariantConstraintsModal';
import PublishDynamicPageVersionDrawer from '../../modals/PublishDynamicPageVersionDrawer';

export default function ManageDynamicPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const { selectedDynamicPage } = useContext(DashboardContext);
  const { variants, websites, hasChangesToDeploy, primaryDomain } =
    useContext(DynamicPageContext);

  const [createVariantModalOpen, setCreateVariantModalOpen] =
    useState<CreateVariantData>();
  const [copyLinkModalOpen, setCopyLinkModalOpen] = useState(false);
  const [openEditor, setOpenEditor] = useState<string>();
  const [editVariantConstraintsModelOpen, setEditVariantConstraintsModelOpen] =
    useState<{ variantId: string }>();
  const [
    publishDynamicPageVersionDrawerOpen,
    setPublishDynamicPageVersionDrawerOpen,
  ] = useState(false);
  const [openVersionsHistoryDrawer, setOpenVersionsHistoryDrawer] =
    useState(false);
  const [deleteConfirmationModalOpen, setDeleteConfirmationModalOpen] =
    useState<{
      entity: string;
      name: string;
      onConfirm: () => void | Promise<void>;
    }>();

  const tabs: Tab[] = useMemo(() => {
    const currentTab = location.pathname.split('/')[5];
    return [
      { id: 'details', name: 'Details' },
      { id: 'adMap', name: 'Ad Map' },
      { id: 'leads', name: 'Leads' },
      { id: 'analytics', name: 'Analytics' },
      { id: 'settings', name: 'Settings' },
    ].map((t) => ({ ...t, current: t.id === currentTab }));
  }, [location.pathname]);

  if (!variants || !selectedDynamicPage) {
    return (
      <div className="flex w-full justify-center pt-24">
        <Loader />
      </div>
    );
  }

  if (
    selectedDynamicPage?.data().creationStatus === 'pending' &&
    !websites?.length
  ) {
    return (
      <div className="flex h-full items-center justify-center">
        <Lottie
          animationData={websiteBuildingAnimation}
          loop
          className="mx-auto max-w-md"
        />
      </div>
    );
  }

  if (variants.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <NoVariantExists
          onCreateClick={(v) => setCreateVariantModalOpen({ approach: v.type })}
        />
        <CreateVariantModal
          defaultData={createVariantModalOpen}
          open={!!createVariantModalOpen}
          onClose={() => setCreateVariantModalOpen(undefined)}
        />
      </div>
    );
  }

  return (
    <>
      <EditorModal
        websiteId={openEditor || ''}
        open={!!openEditor}
        onClose={() => setOpenEditor(undefined)}
      />
      <CopyDynamicPageLinkModal
        open={copyLinkModalOpen}
        onClose={() => setCopyLinkModalOpen(false)}
      />
      <EditVariantConstraintsModal
        variantId={editVariantConstraintsModelOpen?.variantId}
        open={!!editVariantConstraintsModelOpen}
        onClose={() => setEditVariantConstraintsModelOpen(undefined)}
      />
      <PublishDynamicPageVersionDrawer
        open={publishDynamicPageVersionDrawerOpen}
        onClose={() => setPublishDynamicPageVersionDrawerOpen(false)}
      />
      <DynamicPageVersionsHistoryDrawer
        open={openVersionsHistoryDrawer}
        onClose={() => setOpenVersionsHistoryDrawer(false)}
      />
      <AlertModal
        type={'warn'}
        open={!!deleteConfirmationModalOpen}
        onClose={() => setDeleteConfirmationModalOpen(undefined)}
        title={`Are you sure you wish to delete ${deleteConfirmationModalOpen?.entity} "${deleteConfirmationModalOpen?.name}"?`}
        description="This action cannot be undone."
        actions={[
          {
            title: 'Cancel',
            type: 'secondary',
            onClick: () => setDeleteConfirmationModalOpen(undefined),
          },
          {
            title: 'Delete',
            type: 'primary',
            color: 'negative',
            onClick: async () => {
              await deleteConfirmationModalOpen?.onConfirm();
              setDeleteConfirmationModalOpen(undefined);
            },
          },
        ]}
      />
      <CreateVariantModal
        defaultData={createVariantModalOpen}
        open={!!createVariantModalOpen}
        onClose={() => setCreateVariantModalOpen(undefined)}
      />
      <div className="flex min-h-full max-w-(--breakpoint-2xl) flex-col">
        <div className="absolute top-0 z-10 hidden h-8 w-full bg-white lg:block"></div>
        <div className="top-8 z-10 flex flex-col bg-white lg:sticky">
          <div className="flex flex-col items-stretch justify-between gap-4 sm:flex-row sm:items-center sm:items-start sm:gap-0">
            <div className="sm:flex-auto">
              <h1 className="text-2xl leading-6 font-semibold text-gray-900">
                {selectedDynamicPage.data().name}
              </h1>
              <p
                className="mt-2 cursor-pointer text-sm text-blue-500 underline"
                onClick={() => {
                  setCopyLinkModalOpen(true);
                }}
              >
                {`https://${primaryDomain?.data().domain}/${selectedDynamicPage.data().path}`}
              </p>
            </div>

            <div className="flex items-center gap-3">
              {hasChangesToDeploy && (
                <div className="pe-2">
                  <Badge title="Changes to Publish" hideDot color="yellow" />
                </div>
              )}
              <div className="flex-1 sm:hidden" />
              {/* <DashboardAnalyticsRangeSelector size="normal" /> */}
              <Button
                variant="primary"
                onClick={() => {
                  setPublishDynamicPageVersionDrawerOpen(true);
                }}
                className="shrink-0"
              >
                Publish
              </Button>
              <MenuButton
                items={[
                  {
                    title: 'Versions History',
                    onClick: () => {
                      setOpenVersionsHistoryDrawer(true);
                    },
                  },
                ]}
              />
            </div>
          </div>
          <Tabs
            className="mt-4"
            tabs={tabs}
            onTabClick={(tab) => {
              navigate(`./${tab}`);
            }}
          />
        </div>

        <Outlet />
      </div>
    </>
  );
}
